import axios from 'axios'
import hash from 'object-hash'
import nestedProperty from 'nested-property'
import axiosErrorHandler from './error'
import {
  singular,
  plural,
  translate,
  makeData,
} from '@/utils'

const makeFileName = (module, params, headers) => (
  headers['content-disposition'] !== undefined
    ? /filename="(.*)"/.exec(headers['content-disposition'])[1]
    : `${module}.${params.format}`
)

export class APIClient {
  constructor (rootUrl) {
    this.rootUrl = rootUrl
    this.apiKey = null
    this.delay = 5 * 50000
    this.hashOptions = {
      respectType: false,
    }
    this.defaultOpts = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': null,
      },
    }
    this.caches = {}
  }

  setCacheExpire (delay) {
    this.delay = delay * 60000
    // console.log('delay is now', this.delay)
  }

  hasAPIKey () {
    return this.apiKey !== null
  }

  setAPIKey (key) {
    this.apiKey = key
    // Set custom header for all subsequent requests
    this.defaultOpts.headers['Authorization'] = `Bearer ${key}`
  }
  clearAPIKey () {
    // delete the API key
    this.defaultOpts.headers['Authorization'] = null
    this.apiKey = null
  }

  cacheQuery (module, opts, data) {
    let key = hash(opts, this.hashOptions)
    let delay = Date.now() + this.delay
    nestedProperty.set(this.caches, `${module}.${key}`, { opts, data, delay })
  }

  inCache (module, key) {
    return nestedProperty.get(this.caches, `${module}.${key}`)
      ? this.caches[module][key].delay > Date.now()
      : false
  }

  dropCache (module) {
    console.log('dropping cache for', module)
    this.caches[module] = {}
  }

  getFromCache (module, key) {
    console.log('getting from cache:', module)
    return nestedProperty.get(this.caches, `${module}.${key}.data`)
  }

  _axios (opts) {
    return new Promise((resolve, reject) => {
      // console.log(opts)
      const { module, method } = opts
      let toHash = {url: opts.url, params: opts.params}
      let key = hash(toHash, this.hashOptions)
      if (this.inCache(module, key) && method === 'get') {
        // console.log('found in cache')
        resolve(this.getFromCache(module, key))
      } else {
        if (method !== 'get') {
          this.dropCache(module)
        }
        axios(opts)
          .then(response => {
            this.cacheQuery(module, toHash, response.data)
            resolve(response.data)
          })
          .catch(axiosErrorHandler)
          .catch(reject)
      }
    })
  }

  _get (module, path, params) {
    let url = `${this.rootUrl}/${path}`
    const opts = {
      ...this.defaultOpts,
      url: url,
      params,
      method: 'get',
      module,
    }
    return this._axios(opts)
  }
  _put (module, path, data, params) {
    let url = `${this.rootUrl}/${path}`
    const { newData, method } = makeData(data, 'put')
    const opts = {
      ...this.defaultOpts,
      url,
      params,
      data: newData,
      method,
      module,
    }
    return this._axios(opts)
  }
  _post (module, path, data, params) {
    let url = `${this.rootUrl}/${path}`
    const { newData, method } = makeData(data, 'post')
    const opts = {
      ...this.defaultOpts,
      url,
      params,
      data: newData,
      method,
      module,
    }
    return this._axios(opts)
  }
  _delete (module, path, params) {
    let url = `${this.rootUrl}/${path}`
    const opts = {
      ...this.defaultOpts,
      url,
      params,
      method: 'delete',
      module,
    }
    return this._axios(opts)
  }

  _download (module, path, data, params, callback) {
    let url = `${this.rootUrl}/${path}`
    const opts = {
      ...this.defaultOpts,
      url,
      params,
      responseType: 'blob',
      data: data,
      onUploadProgress: callback,
      method: 'post',
      module,
    }
    // console.log(opts)
    return new Promise((resolve, reject) => {
      return axios(opts)
        .then((response) => {
          // console.log('response', response)
          let filename = makeFileName(module, params, response.headers)
          resolve({ filename, blob: response.data })
        })
        .catch(axiosErrorHandler)
        .catch(reject)
    })
  }

  login (creds) {
    this.clearAPIKey()
    return new Promise((resolve, reject) => {
      this._post('login', 'login', creds)
        .then(back => {
          let apiKey = back.token
          this.setAPIKey(apiKey)
          resolve(apiKey)
        })
        .catch(reject)
    })
  }

  logout () {
    return new Promise((resolve, reject) => {
      this.clearAPIKey()
      resolve(true)
    })
  }

  fetchAll (key, params = {}) {
    let qkey = translate(key)
    return new Promise((resolve, reject) => {
      this._get(key, qkey, params)
        .then(response => {
          let newKey = plural(key)
          let customResponse = response[newKey] ? response[newKey] : response
          resolve(customResponse)
        })
        .catch(reject)
    })
  }

  fetchCustom (key, path, params = {}) {
    return new Promise((resolve, reject) => {
      this._get(key, path, params)
        .then(response => {
          let newKey = plural(key)
          let customResponse = response[newKey] ? response[newKey] : response
          resolve(customResponse)
        })
        .catch(reject)
    })
  }

  fetchOne (key, id, params = {}) {
    let qkey = translate(key)
    return new Promise((resolve, reject) => {
      this._get(key, `${qkey}/${id}`, params)
        .then(data => {
          resolve(data[singular(key)])
        })
        .catch(reject)
    })
  }

  create (key, payload) {
    let qkey = translate(key)
    return new Promise((resolve, reject) => {
      this._post(key, qkey, payload)
        .then(data => {
          resolve(data[singular(key)])
        })
        .catch(reject)
    })
  }

  postCustom (key, path, data, params = {}) {
    return new Promise((resolve, reject) => {
      this._post(key, path, data, params)
        .then(response => {
          resolve(response[singular(key)])
        })
        .catch(reject)
    })
  }

  putCustom (key, path, data, params = {}) {
    return new Promise((resolve, reject) => {
      this._put(key, path, data, params)
        .then(response => {
          // console.log(response)
          resolve(response[singular(key)])
        })
        .catch(reject)
    })
  }

  update (key, id, payload, params = {}) {
    let qkey = translate(key)
    return new Promise((resolve, reject) => {
      this._put(key, `${qkey}/${id}`, payload, params)
        .then(response => {
          resolve(response[singular(key)])
        })
        .catch(reject)
    })
  }

  exportTable (
    key,
    accessors,
    params = {},
    callback = event => console.log(event)
  ) {
    let qkey = translate(key)
    return new Promise((resolve, reject) => {
      this._download(key, `${qkey}/export`, accessors, params, callback)
        .then(response => {
          resolve(response)
        })
        .catch(reject)
    })
  }

  delete (key, id, params = {}) {
    let qkey = translate(key)
    return new Promise((resolve, reject) => {
      this._delete(key, `${qkey}/${id}`, params)
        .then(response => {
          resolve(true)
        })
        .catch(reject)
    })
  }

  batchDelete (key, params = {}) {
    let qkey = translate(key)
    return new Promise((resolve, reject) => {
      this._delete(key, `${qkey}/batch`, params)
        .then(response => {
          resolve(true)
        })
        .catch(reject)
    })
  }

  batchPut (key, data, params = {}) {
    let qkey = translate(key)
    return new Promise((resolve, reject) => {
      this._put(key, `${qkey}/batch`, data, params)
        .then(response => {
          let newKey = plural(key)
          let customResponse = response[newKey] ? response[newKey] : response
          resolve(customResponse)
        })
        .catch(reject)
    })
  }
}
// export pre-configured singleton apiClient instance
export let apiClient = new APIClient(process.env.VUE_APP_API_REST_URL)
